'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type DeliveryStatus =
  | 'matched' |'payment_escrowed' |'pickup_confirmed' |'in_transit' |'delivered' |'completed';

interface Delivery {
  id: string;
  status: DeliveryStatus;
  matchedAt: string | null;
  paymentEscrowedAt: string | null;
  pickupConfirmedAt: string | null;
  inTransitAt: string | null;
  deliveredAt: string | null;
  completedAt: string | null;
}

interface TrackingEvent {
  id: string;
  event_type: string;
  title: string;
  description: string;
  location_name: string;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
}

interface Props {
  delivery: Delivery;
  events: TrackingEvent[];
}

const STATUS_STAGES: { id: DeliveryStatus; label: string; desc: string; icon: string; tsKey: keyof Delivery }[] = [
  { id: 'matched',          label: 'Traveler Matched',   desc: 'Bid accepted, delivery agreement confirmed',       icon: 'UserCheckIcon',    tsKey: 'matchedAt' },
  { id: 'payment_escrowed', label: 'Payment Escrowed',   desc: 'Funds secured in escrow until delivery confirmed', icon: 'LockClosedIcon',   tsKey: 'paymentEscrowedAt' },
  { id: 'pickup_confirmed', label: 'Pickup Confirmed',   desc: 'Traveler collected the parcel at origin',          icon: 'QrCodeIcon',       tsKey: 'pickupConfirmedAt' },
  { id: 'in_transit',       label: 'In Transit',         desc: 'Parcel is en route to destination',                icon: 'TruckIcon',        tsKey: 'inTransitAt' },
  { id: 'delivered',        label: 'Delivered',          desc: 'Parcel arrived at destination city',               icon: 'MapPinIcon',       tsKey: 'deliveredAt' },
  { id: 'completed',        label: 'Completed',          desc: 'Receipt confirmed, escrow released to traveler',   icon: 'CheckCircleIcon',  tsKey: 'completedAt' },
];

const STATUS_ORDER: DeliveryStatus[] = ['matched', 'payment_escrowed', 'pickup_confirmed', 'in_transit', 'delivered', 'completed'];

const EVENT_ICON: Record<string, string> = {
  checkpoint:   'MapPinIcon',
  gps_ping:     'SignalIcon',
  eta_update:   'ClockIcon',
  completed:    'CheckBadgeIcon',
  pickup:       'QrCodeIcon',
  in_transit:   'TruckIcon',
  delivered:    'FlagIcon',
  default:      'InformationCircleIcon',
};

function fmtMinute(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-KE', {
    day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

export default function StatusTimeline({ delivery, events }: Props) {
  const currentIdx = STATUS_ORDER.indexOf(delivery.status);
  const [expanded, setExpanded] = useState<Set<string>>(new Set([delivery.status]));

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Group events by stage
  const eventsByStage: Record<string, TrackingEvent[]> = {};
  events.forEach(ev => {
    const stage = ev.event_type === 'gps_ping' || ev.event_type === 'checkpoint' || ev.event_type === 'eta_update' ?'in_transit'
      : ev.event_type === 'pickup'|| ev.event_type === 'pickup_confirmed' ?'pickup_confirmed'
        : ev.event_type === 'completed' ?'completed'
          : ev.event_type === 'delivered' ?'delivered' :'in_transit';
    if (!eventsByStage[stage]) eventsByStage[stage] = [];
    eventsByStage[stage].push(ev);
  });

  return (
    <div className="card-elevated p-4 sm:p-5">
      <div className="flex items-center justify-between mb-4 sm:mb-5">
        <h3 className="text-sm font-700 text-foreground">Status Timeline</h3>
        <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Minute-precision</span>
      </div>

      <div className="relative">
        {STATUS_STAGES.map((stage, idx) => {
          const isCompleted = idx < currentIdx;
          const isCurrent   = idx === currentIdx;
          const isPending   = idx > currentIdx;
          const isExpanded  = expanded.has(stage.id);
          const ts          = delivery[stage.tsKey] as string | null;
          const stageEvents = eventsByStage[stage.id] || [];

          return (
            <div key={stage.id} className="flex gap-3 sm:gap-4 relative">
              {/* Connector */}
              {idx < STATUS_STAGES.length - 1 && (
                <div className="absolute left-4 top-8 w-0.5 z-0" style={{ height: 'calc(100% - 2rem)' }}>
                  <div className={`w-full h-full transition-colors duration-500 ${isCompleted ? 'bg-success' : isCurrent ? 'bg-primary/40' : 'bg-border'}`} />
                </div>
              )}

              {/* Icon bubble */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 transition-all duration-300
                ${isCompleted ? 'bg-success border-success' : isCurrent ? 'bg-primary border-primary' : 'bg-card border-border'}`}>
                {isCompleted
                  ? <Icon name="CheckCircleIcon" size={14} className="text-white" />
                  : <Icon name={stage.icon as any} size={14} className={isCurrent ? 'text-white' : 'text-muted-foreground'} />
                }
              </div>

              {/* Content */}
              <div className={`pb-4 sm:pb-5 flex-1 min-w-0 ${idx === STATUS_STAGES.length - 1 ? 'pb-0' : ''}`}>
                <button
                  onClick={() => !isPending && toggle(stage.id)}
                  disabled={isPending}
                  className="w-full flex items-start justify-between gap-2 text-left group min-h-[44px] py-1"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm font-600 ${isCurrent ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {stage.label}
                      </p>
                      {isCurrent && (
                        <span className="flex items-center gap-1 text-[10px] font-600 text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                          Current
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <p className={`text-xs ${isPending ? 'text-muted-foreground/50' : 'text-muted-foreground'}`}>{stage.desc}</p>
                      {ts && !isPending && (
                        <>
                          <span className="text-muted-foreground/40 text-xs">·</span>
                          <span className="flex items-center gap-1 text-[10px] font-500 text-muted-foreground tabular-nums">
                            <Icon name="ClockIcon" size={10} />
                            {fmtMinute(ts)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {!isPending && (
                    <Icon
                      name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                      size={16}
                      className={`flex-shrink-0 mt-1 transition-transform duration-200 ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`}
                    />
                  )}
                </button>

                {/* Expanded: stage events */}
                {!isPending && isExpanded && (
                  <div className="mt-2 pl-3 border-l-2 border-border space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    {stageEvents.length === 0 && (
                      <p className="text-xs text-muted-foreground/60 italic">No detailed events for this stage yet.</p>
                    )}
                    {stageEvents.map(ev => (
                      <div key={ev.id} className="flex items-start gap-2.5 py-2 border-b border-border/40 last:border-0">
                        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name={(EVENT_ICON[ev.event_type] || EVENT_ICON.default) as any} size={11} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xs font-600 text-foreground">{ev.title}</p>
                            <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{fmtMinute(ev.created_at)}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{ev.description}</p>
                          {ev.location_name && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                              <Icon name="MapPinIcon" size={9} />
                              {ev.location_name}
                            </span>
                          )}
                          {ev.latitude && ev.longitude && (
                            <span className="ml-2 text-[10px] text-primary font-500">
                              ({ev.latitude.toFixed(4)}, {ev.longitude.toFixed(4)})
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    {isCompleted && ts && (
                      <div className="flex items-center gap-1.5 pt-1">
                        <Icon name="CheckBadgeIcon" size={12} className="text-success" />
                        <span className="text-[10px] font-600 text-success">Completed at {fmtMinute(ts)}</span>
                      </div>
                    )}
                    {isCurrent && (
                      <div className="flex items-center gap-1.5 pt-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                        <span className="text-[10px] font-600 text-primary">In progress — updates arriving in real time</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
